import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlarmClock, Check, Copy, Plus, Scale, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { OrgLogo } from "@/lib/banks";
import { Verdict } from "@/components/common/Verdict";

const KINDS = { habitation: "Habitation", multirisque: "Multirisque", appareils: "Appareils nomades", auto: "Auto", autre: "Autre" };
const fr = (d) => new Date(d).toLocaleDateString("fr-FR");
const STATUS = { soon: "tone-peach", expired: "tone-rose", ok: "tone-mint" };

const GuaranteeMatrix = ({ d }) => {
  const { money } = useFormat();
  if (!d.guarantees.length) return <p className="text-sm text-muted-foreground">Aucune garantie renseignée.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-border" data-testid="guarantee-matrix">
      <table className="w-full min-w-[32rem] text-sm">
        <thead><tr className="bg-secondary/60 text-left text-xs text-muted-foreground"><th className="p-2.5">Garantie</th>
          {d.contracts.map((c) => <th key={c.id} className="p-2.5 font-semibold"><span className="flex items-center gap-2"><OrgLogo name={c.org_name} className="h-6 w-6" /><span>{KINDS[c.kind]}<span className="block font-mono font-normal">{c.number}</span></span></span></th>)}</tr></thead>
        <tbody>{d.guarantees.map((g) => (
          <tr key={g.label} className="border-t border-border" data-testid={`matrix-row-${g.label}`}>
            <td className="p-2.5 font-medium">{g.label}{Object.keys(g.cells).length > 1 && <span className="tone-chip tone-peach ml-2 rounded-full px-2 py-0.5 text-[10px]">×{Object.keys(g.cells).length}</span>}</td>
            {d.contracts.map((c) => { const x = g.cells[c.id]; return (
              <td key={c.id} className="num p-2.5 text-xs">{x ? <span className="flex items-start gap-1.5"><OrgLogo name={c.org_name} className="h-4 w-4 rounded" /><span>{x.ceiling != null ? money(x.ceiling) : "Inclus"}{x.deductible != null && <span className="block text-muted-foreground">franchise {money(x.deductible)}</span>}</span></span> : <X className="h-3.5 w-3.5 text-muted-foreground/50" />}</td>); })}
          </tr>))}</tbody>
      </table>
    </div>
  );
};

const Deadlines = ({ d }) => {
  const { money } = useFormat();
  if (!d.deadlines.length) return <p className="text-sm text-muted-foreground">Aucune date d'échéance renseignée.</p>;
  return (
    <ul className="space-y-2" data-testid="deadlines-list">
      {d.deadlines.map((x) => (
        <li key={x.contract_id} className="flex flex-wrap items-start gap-3 rounded-xl border border-border p-3" data-testid={`deadline-${x.contract_id}`}>
          <span className={`tone-chip ${STATUS[x.status]} flex h-12 w-14 shrink-0 flex-col items-center justify-center rounded-xl text-xs`}><b className="num text-base" data-testid={`deadline-days-${x.contract_id}`}>{x.days_left}</b>jours</span>
          <OrgLogo name={x.org_name} className="h-9 w-9" />
          <div className="min-w-0 flex-1 basis-56 text-sm"><div className="font-semibold">{KINDS[x.kind]} · {x.org_name}</div>
            <div className="text-xs text-muted-foreground">N° {x.number} · échéance {fr(x.end)} · {money(x.premium_year)}/an</div>
            <p className="mt-1 text-xs">{x.advice}</p></div>
        </li>))}
    </ul>
  );
};

const Duplicates = ({ d }) => {
  const { money } = useFormat();
  if (!d.duplicates.length) return <p className="text-sm text-muted-foreground" data-testid="duplicates-none">Aucun doublon détecté entre vos contrats.</p>;
  return (
    <ul className="space-y-2" data-testid="duplicates-list">
      {d.duplicates.map((x, i) => (
        <li key={i} className="rounded-xl border border-border p-3 text-sm" data-testid={`duplicate-${i}`}>
          <div className="flex flex-wrap items-center gap-2"><Copy className="h-4 w-4 text-warning" /><b className="flex-1">{x.label}</b>
            {x.saving_year && <span className="tone-chip tone-mint rounded-full px-2.5 py-0.5 text-xs font-semibold">jusqu'à {money(x.saving_year)}/an</span>}</div>
          <p className="my-1 text-xs text-muted-foreground">{x.text}</p>
          <div className="flex flex-wrap gap-1.5">{x.contracts.map((c) => <span key={c.id} className="tone-chip tone-lilac rounded-full px-2.5 py-0.5 text-xs">{KINDS[c.kind]} · {c.number}</span>)}</div>
        </li>))}
    </ul>
  );
};

const OfferDialog = ({ c, labels, onOpenChange }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const [v, setV] = useState({ insurer: "", premium_month: "", guarantees: labels });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/households/${active.id}/insurance/${c.contract_id}/offers`, { ...v, premium_month: Number(v.premium_month) });
      qc.invalidateQueries({ queryKey: ["insurance"] });
      toast.success("Devis ajouté au comparateur");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = (l, on) => setV({ ...v, guarantees: on ? [...v.guarantees, l] : v.guarantees.filter((x) => x !== l) });
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
        <DialogHeader><DialogTitle className="font-heading">Ajouter un devis · {c.number}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input value={v.insurer} onChange={(e) => setV({ ...v, insurer: e.target.value })} required minLength={2} placeholder="Assureur proposé" data-testid="offer-insurer-input" />
          <Input type="number" min="0.01" step="0.01" value={v.premium_month} onChange={(e) => setV({ ...v, premium_month: e.target.value })} required placeholder="Cotisation mensuelle €" data-testid="offer-premium-input" />
          <div className="text-xs font-semibold text-muted-foreground">Garanties incluses dans le devis</div>
          <div className="space-y-1.5 rounded-xl border border-border p-3">{labels.map((l, i) => (
            <label key={l} className="flex items-center gap-2 text-sm"><Checkbox checked={v.guarantees.includes(l)} onCheckedChange={(on) => toggle(l, on)} data-testid={`offer-guarantee-${i}`} />{l}</label>))}</div>
          <Button type="submit" className="w-full rounded-full" data-testid="offer-submit-button">Comparer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CompareCard = ({ c, labels, canManage }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { money } = useFormat();
  const [open, setOpen] = useState(false);
  const del = async (oid) => { try { await api.delete(`/households/${active.id}/insurance/${c.contract_id}/offers/${oid}`); qc.invalidateQueries({ queryKey: ["insurance"] }); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="rounded-xl border border-border p-4 text-sm" data-testid={`compare-${c.contract_id}`}>
      <div className="flex flex-wrap items-center gap-2"><OrgLogo name={c.org_name} className="h-7 w-7" /><b className="flex-1">{KINDS[c.kind]} · {c.number}</b><span className="num font-semibold">{c.premium_month != null ? `${money(c.premium_month, { maximumFractionDigits: 2 })}/mois` : "—"}</span></div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2"><Verdict b={c.benchmark} testid={`compare-verdict-${c.contract_id}`} />
        {c.market_ref && <span className="text-xs text-muted-foreground" data-testid={`compare-market-${c.contract_id}`}>Repère indicatif : {money(c.market_ref, { maximumFractionDigits: 2 })}/mois</span>}</div>
      <ul className="mt-3 space-y-1.5">{c.offers.map((o) => (
        <li key={o.id} className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 ${o.complete ? "bg-mint/40" : "bg-secondary/60"}`} data-testid={`offer-${o.id}`}>
          <span className="min-w-0 flex-1 basis-40 font-medium">{o.insurer}{!o.complete && <span className="block text-xs font-normal text-destructive">Manque : {o.missing.join(", ")}</span>}</span>
          <span className="num">{money(o.premium_month, { maximumFractionDigits: 2 })}/mois</span>
          {o.saving_year != null && <span className={`num text-xs font-semibold ${o.saving_year > 0 ? "text-success" : "text-destructive"}`} data-testid={`offer-saving-${o.id}`}>{o.saving_year > 0 ? "Économie " : "Surcoût "}{money(Math.abs(o.saving_year))}/an</span>}
          {canManage && <button onClick={() => del(o.id)} aria-label="Retirer le devis" data-testid={`offer-delete-${o.id}`}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>}
        </li>))}</ul>
      {!c.offers.length && <p className="mt-2 text-xs text-muted-foreground">Ajoutez les devis reçus pour les comparer garantie par garantie.</p>}
      {canManage && <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={() => setOpen(true)} data-testid={`offer-new-${c.contract_id}`}><Plus className="mr-1 h-3.5 w-3.5" />Ajouter un devis</Button>}
      {open && <OfferDialog c={c} labels={labels} onOpenChange={setOpen} />}
    </li>
  );
};

const Comparator = ({ d, canManage }) => (
  <div className="space-y-3">
    <ul className="space-y-3" data-testid="comparator-list">{d.comparator.map((c) => (
      <CompareCard key={c.contract_id} c={c} canManage={canManage} labels={d.guarantees.filter((g) => g.cells[c.contract_id]).map((g) => g.label)} />))}</ul>
    <p className="text-xs text-muted-foreground">Repères marché : moyennes indicatives CASHMY, à vérifier avec de vrais devis. Une offre n'est « complète » que si elle reprend toutes vos garanties.</p>
  </div>
);

export const InsuranceReview = ({ canManage }) => {
  const { active } = useHousehold();
  const { money } = useFormat();
  const { data: d } = useQuery({ queryKey: ["insurance", "review", active?.id], queryFn: () => api.get(`/households/${active.id}/insurance-review`).then((r) => r.data), enabled: !!active });
  if (!d || !d.contracts.length) return null;
  const soon = d.deadlines.filter((x) => x.status === "soon").length;
  return (
    <div className="card-surface space-y-4 p-5" data-testid="insurance-review">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><div className="text-xs text-muted-foreground">Cotisations annuelles</div><div className="num text-2xl font-bold" data-testid="review-total-year">{money(d.total_year)}</div></div>
        <div><div className="text-xs text-muted-foreground">Économie possible</div><div className="num text-2xl font-bold text-success" data-testid="review-saving-year">{money(d.potential_saving_year)}/an</div></div>
        <div><div className="text-xs text-muted-foreground">Échéances sous 60 jours</div><div className="num text-2xl font-bold" data-testid="review-soon-count">{soon}</div></div>
      </div>
      <Tabs defaultValue="garanties">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="garanties" data-testid="review-tab-guarantees">Garanties</TabsTrigger>
          <TabsTrigger value="echeances" data-testid="review-tab-deadlines"><AlarmClock className="mr-1 h-3.5 w-3.5" />Échéances</TabsTrigger>
          <TabsTrigger value="doublons" data-testid="review-tab-duplicates"><Copy className="mr-1 h-3.5 w-3.5" />Doublons ({d.duplicates.length})</TabsTrigger>
          <TabsTrigger value="comparateur" data-testid="review-tab-comparator"><Scale className="mr-1 h-3.5 w-3.5" />Comparateur</TabsTrigger>
        </TabsList>
        <TabsContent value="garanties"><GuaranteeMatrix d={d} /></TabsContent>
        <TabsContent value="echeances"><Deadlines d={d} /></TabsContent>
        <TabsContent value="doublons"><Duplicates d={d} /></TabsContent>
        <TabsContent value="comparateur"><Comparator d={d} canManage={canManage} /></TabsContent>
      </Tabs>
    </div>
  );
};
